import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Download } from "lucide-react";
import { BROCHURE_URL, CONTACT_EMAIL, CONTACT_PHONE } from "../lib/api";
import { scrollToSection } from "./SmoothScroll";
import { useLang } from "../i18n";

const NAV = [
  { key: "species", href: "#species", id: "species" },
  { key: "heritage", href: "#heritage", id: "heritage" },
  { key: "reach", href: "#reach", id: "global-reach" },
  { key: "quote", href: "#quote", id: "request-a-quote" },
];

export const Footer = () => {
  const { t } = useLang();
  const go = (href) => scrollToSection(href);

  const CONTACTS = [
    { icon: Mail, label: t("footer.email"), value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
    { icon: Phone, label: t("footer.phone"), value: CONTACT_PHONE, href: `tel:${CONTACT_PHONE}` },
    { icon: MapPin, label: t("footer.hq"), value: t("footer.hqValue") },
  ];

  return (
    <footer
      data-testid="main-footer"
      className="relative overflow-hidden bg-brand-blue pt-24 text-brand-bone"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 border-b border-brand-bone/15 pb-20 md:grid-cols-12">
          <div className="md:col-span-5">
            <img src="/logo.png" alt="7 Seas Group USA" className="h-16 w-auto brightness-0 invert" />
            <p className="mt-6 max-w-sm font-light leading-relaxed text-brand-bone/70">
              {t("footer.blurb")}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="mb-6 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-timber">
              {t("footer.navigate")}
            </h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <button
                    data-testid={`footer-link-${n.id}`}
                    onClick={() => go(n.href)}
                    className="font-light text-brand-bone/80 transition-colors duration-300 hover:text-brand-timber"
                  >
                    {t(`nav.${n.key}`)}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href={BROCHURE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-brochure-link"
                  className="inline-flex items-center gap-2 font-light text-brand-bone/80 transition-colors duration-300 hover:text-brand-timber"
                >
                  <Download size={14} /> {t("footer.brochure")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="mb-6 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-timber">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-4">
              {CONTACTS.map((c) => {
                const Icon = c.icon;
                return (
                  <li key={c.label} className="flex items-start gap-3">
                    <Icon size={18} className="mt-0.5 text-brand-timber" strokeWidth={1.5} />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bone/50">
                        {c.label}
                      </div>
                      {c.href ? (
                        <a href={c.href} className="font-light text-brand-bone/85 transition-colors duration-300 hover:text-brand-timber">{c.value}</a>
                      ) : (
                        <div className="font-light text-brand-bone/85">{c.value}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 py-8 md:flex-row">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/50">
            © {new Date().getFullYear()} 7 Seas Group USA · {t("footer.rights")}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/50">
            {t("footer.family")}
          </p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none select-none px-6 md:px-12"
      >
        <h2 className="whitespace-nowrap font-serif text-[24vw] font-medium leading-[0.75] tracking-tight text-brand-bone/[0.06] md:text-[18vw]">
          7 SEAS GROUP
        </h2>
      </motion.div>
    </footer>
  );
};
